import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'reactstrap';
import './subscribe-modal.css';
import { connect } from 'react-redux';
import { useNavigation } from '../../../shared/utils/useNavigation';
import { reportError } from '../../../shared/sentry';
import { isUserLoggedIn } from '../../../shared/utils/function';
import {
  DEFAULT_USER_AVATAR,
  defaultProfileBanner,
  ROLE,
  OFFER_TYPE,
  PAYMENT_INFO,
  TRANSACTION_TYPE,
  SUBSCRIPTION_STATUS_REFRESH_DELAY_MS,
} from '../../../shared/constants/common';
import {
  showError,
  showInfo,
  showSignInRequired,
  confirmDialog,
  showCustomAlert,
  closeAlert,
  showPaymentProcessing,
  showPaymentLoading,
  showFreeTrialLoading
} from '../../../shared/utils/swalHandler';
import { showSubscriptionSuccessPushToast } from '../../../shared/pusNotification/useManualToasts';
import CCBillModal from '../../Pages/CCbill/CCBillModal';
import { useModalOpen } from "../../../shared/utils/modalUtils.js";
import { setPaymentFlowActive } from "../../../shared/utils/config/paymentFlowGuard";
import { formatAmountWithTwoDecimalPlaces } from '../../../shared/utils/function';
import { checkPromotion } from '../../../shared/actions/promotionAction';
import {
  unsubscribe,
  createSubscription,
  checkSubscription
} from '../../../shared/actions/subscriptionAction';
import { charge as chargeCcbill } from '../../../shared/actions/ccbillAction';
import { updatePaymentTokenId } from '../../../shared/actions/userActions';
import userService from '../../../shared/services/userService';
import {
  getSubscriptionPrice,
  buildPurchasePayload,
  getVatAndServiceFeeAppliedText
} from '../../../shared/utils/paymentUtil';
import { getBundlesInfo } from './SubscriptionUtil';

// Constants
const DEFAULT_PRICE = '9.99';
const CCBILL_CURRENCY_CODE = Number(process.env.REACT_APP_CCBILL_CURRENCY_CODE);
const CCBILL_DEFAULT_PERIOD = 30;

const SWAL_CONFIG = {
  confirmButtonColor: '#F334BE',
  customClass: {
    title: 'swal-unsub-title',
    htmlContainer: 'swal-unsub-body'
  }
};

/**
 * SubscribeModalLoggedIn Component
 * Modal component for subscribing to content creators
 */
const SubscribeModalLoggedIn = ({
  creatorInfo,
  btnText,
  isButtonClickable = true,
  isFreeTrialSubscription = false,
  isSubscribed = false,
  isOfferActiveOfUser = false,
  isRenew = false,
  handleCallback = () => { },
  user, // Redux state
  renderTrigger = null,
  /** Increment from parent (e.g. after "Chat Locked" Subscribe) to open the modal programmatically */
  openSignal = 0,
}) => {
  const navigate = useNavigation();
  // State management
  const [modal, setModal] = useState(false);
  const [pinnedMessageExpanded, setPinnedMessageExpanded] = useState(false);
  const [isPromotionLoading, setIsPromotionLoading] = useState(false);
  const [promotionData, setPromotionData] = useState(null);
  const [ccBillModalOpen, setCCBillModalOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(null);
  // True when CCBillModal is opened before any subscription exists (no saved
  // card). POST subscriptions then only runs on card-form submit, via
  // prepareDeferredPaymentForm below — never just by opening the card form.
  const [isDeferredCCBill, setIsDeferredCCBill] = useState(false);
  const pendingSubscriptionDataRef = useRef(null);
  const deferredPaymentFormRef = useRef(null);
  const subscriptionCreatedRef = useRef(false);
  const [isBrowser, setIsBrowser] = useState(false);

  useEffect(() => {
    setIsBrowser(true);
  }, []);

  // Add/remove class on HTML tag when modal opens/closes
  useModalOpen(modal);

  // Block signed-URL refresh (page reload) while subscribe payment is in progress
  useEffect(() => {
    const active = modal || ccBillModalOpen;
    setPaymentFlowActive(active);
    return () => setPaymentFlowActive(false);
  }, [modal, ccBillModalOpen]);

  // Computed values
  const loggedIn = isUserLoggedIn();
  const subscriptionPrice = useMemo(
    () => parseFloat(creatorInfo?.user_subsData?.price || DEFAULT_PRICE),
    [creatorInfo]
  );
  const subscriptionPriceWithExtraCharge = parseFloat(subscriptionPrice) + parseFloat(PAYMENT_INFO.extraCharge);

  const getPrice = useMemo(
    () => getSubscriptionPrice(promotionData, creatorInfo?.user_subsData?.real_price, subscriptionPrice),
    [promotionData, creatorInfo?.user_subsData?.real_price, subscriptionPrice]
  );
  const [purchasePayload, setPurchasePayload] = useState(null);

  useEffect(() => {
    const fetchPurchasePayload = async () => {
      // Free trial / first-month discount: POST subscriptions needs recurringPrice
      // = full real price + service fee (0.25) + VAT for post-promo rebills.
      const hasDiscountOrOffer =
        Boolean(promotionData?.active) || getPrice.isFreeTrial;

      const payload = await buildPurchasePayload(
        getPrice.isFreeTrial ? getPrice.originalPrice : getPrice.currentPrice,
        subscriptionPrice,
        user,
        { includeRecurringPrice: hasDiscountOrOffer }
      );
      setPurchasePayload(payload);
    };
    fetchPurchasePayload();
  }, [getPrice, subscriptionPrice, user, promotionData]);

  // Fetch promotion data
  const fetchPromotionData = useCallback(async (creatorId) => {
    setIsPromotionLoading(true);
    try {
      const result = await checkPromotion(creatorId);
      if (result.success && result.data) {
        if (result.data.eligible && result?.data?.promo) {
          setPromotionData(result.data.promo);
        }
      } else {
        setPromotionData(null);
      }
    } catch (error) {
      setPromotionData(null);
    } finally {
      setIsPromotionLoading(false);
    }
  }, []);

  // Reset pinned message expanded when modal closes
  useEffect(() => {
    if (!modal) setPinnedMessageExpanded(false);
  }, [modal]);

  // Toggle modal state
  const toggleModal = useCallback(() => {
    setModal(prevModal => {
      if (!prevModal && !isButtonClickable) {
        return prevModal;
      }
      const newModalState = !prevModal;

      // Fetch promotion data when opening modal
      if (user && user?.rol === ROLE.USER && newModalState) {
        const ccId = creatorInfo?._id;
        if (ccId) {
          fetchPromotionData(ccId);
        }
      }

      return newModalState;
    });
  }, [fetchPromotionData, user, creatorInfo, isButtonClickable]);

  // if openSignal is provided, open the modal programmatically
  useEffect(() => {
    if (!openSignal) return;
    setModal((prevModal) => {
      if (prevModal) return prevModal;
      if (!isButtonClickable) return prevModal;
      if (user && user?.rol === ROLE.USER) {
        const ccId = creatorInfo?._id;
        if (ccId) {
          fetchPromotionData(ccId);
        }
      }
      return true;
    });
  }, [openSignal, isButtonClickable, user, creatorInfo?._id, fetchPromotionData]);

  // Subscribe button handler
  const subscribeBtn = useCallback(async () => {
    if (loggedIn) {
      toggleModal();
    } else {
      const confirmed = await showSignInRequired('subscribe', SWAL_CONFIG);
      if (confirmed) {
        navigate.toLogin();
      }
    }
  }, [loggedIn, toggleModal]);

  // Call unsubscribe API
  const callUnsubscribeAPI = useCallback(async (creatorId, isManualRemove = false) => {
    try {
      await unsubscribe(creatorId, isManualRemove);
    } catch (error) {
      // Error handled silently
      reportError(error, 'SubscribeModalLoggedIn unsubscribe');
    }
  }, []);

  // Handle CCBill payment response
  const handleCCBillPayment = useCallback(async (res, form) => {
    if (res?.data?.paymentOneClick === '1') {
      await showCustomAlert('', res?.data?.paymentOneClickMessage, SWAL_CONFIG);
      if (res?.data?.paymentUrl) {
        window.location.href = res?.data?.paymentUrl;
      }
    } else {
      // Open CCBillModal with payment form and dynamic data
      setPaymentForm(form);
      setCCBillModalOpen(true);
      closeAlert();
    }
  }, []);

  const handleSubscriptionSuccess = useCallback(async () => {
    // Payment completed — the created subscription must no longer be
    // treated as a pending one that cancel/close should roll back.
    subscriptionCreatedRef.current = false;
    deferredPaymentFormRef.current = null;
    pendingSubscriptionDataRef.current = null;
    setIsDeferredCCBill(false);
    setCCBillModalOpen(false);
    closeAlert();
    showSubscriptionSuccessPushToast(creatorInfo);
    await new Promise((resolve) => setTimeout(resolve, SUBSCRIPTION_STATUS_REFRESH_DELAY_MS));
    try {
      await handleCallback();
    } catch (error) {
      reportError(error, 'SubscribeModalLoggedIn subscription success callback');
    }
  }, [creatorInfo, handleCallback]);

  // Charge with existing payment token
  const chargeWithExistingToken = useCallback(async (paymentTokenId, subscriptionData, ccId) => {
    try {
      showPaymentProcessing();

      // Step 1: Get bundles info to get subscription data
      const bundlesResult = await getBundlesInfo(promotionData, purchasePayload);
      if (!bundlesResult.success) {
        throw new Error(bundlesResult.error || 'Failed to get subscription bundles');
      }

      // Step 2: Create subscription to get paymentForm
      const subscriptionResult = await createSubscription(ccId, subscriptionData);

      if (!subscriptionResult.success || !subscriptionResult.data) {
        throw new Error('Failed to create subscription');
      }

      const paymentForm = subscriptionResult.data.paymentForm;

      // Step 3: Extract passThroughInfo from paymentForm
      const passThroughInfo = paymentForm
        ? paymentForm.filter(f => f.name.startsWith('c_')).map(f => ({
          name: f.name,
          value: f.value
        }))
        : [
          { name: 'c_user_id', value: localStorage.getItem('userId') || '' },
          { name: 'c_creator_id', value: creatorInfo?._id || '' },
          { name: 'c_creator_username', value: creatorInfo?.username || '' },
          { name: 'c_payment_type', value: TRANSACTION_TYPE.SUBSCRIPTION }
        ];

      // Step 4: Build charge payload using paymentForm data
      const chargePayload = {
        paymentTokenId: paymentTokenId,
        clientSubacc: paymentForm?.find(f => f.name === 'clientSubacc')?.value || process.env.REACT_APP_CCBILL_SUBACCOUNT,
        currencyCode: paymentForm ? Number(paymentForm.find(f => f.name === 'currencyCode')?.value) : CCBILL_CURRENCY_CODE,
        initialPrice: paymentForm ? Number(paymentForm.find(f => f.name === 'initialPrice')?.value) : subscriptionPrice,
        initialPeriod: paymentForm ? Number(paymentForm.find(f => f.name === 'initialPeriod')?.value) : CCBILL_DEFAULT_PERIOD,
        passThroughInfo
      };

      // Add conditional keys if they exist in paymentForm
      if (paymentForm) {
        const recurringPrice = paymentForm.find(f => f.name === 'recurringPrice')?.value;
        const recurringPeriod = paymentForm.find(f => f.name === 'recurringPeriod')?.value;
        const numRebills = paymentForm.find(f => f.name === 'numRebills')?.value;

        if (recurringPrice) chargePayload.recurringPrice = Number(recurringPrice);
        if (recurringPeriod) chargePayload.recurringPeriod = Number(recurringPeriod);
        if (numRebills) chargePayload.numRebills = Number(numRebills);
      }

      // Discount / offer: prefer client-calculated recurringPrice (realPrice + fee + VAT)
      if (purchasePayload?.recurringPrice != null) {
        chargePayload.recurringPrice = Number(purchasePayload.recurringPrice);
      }

      // Step 5: Call charge API with existing payment token
      const chargeResult = await chargeCcbill(chargePayload);

      if (chargeResult.success && (chargeResult.data?.success || chargeResult.data?.approved)) {
        // Step 6: Update user with paymentTokenId
        await updatePaymentTokenId(paymentTokenId);

        await handleSubscriptionSuccess();
      } else {
        // Payment failed - call unsubscribe API
        await callUnsubscribeAPI(ccId, false);
        await showError(
          'Error!',
          `Payment failed: ${chargeResult.data?.message || chargeResult.data?.declineText || chargeResult.error || 'Payment failed'}`,
          SWAL_CONFIG
        );
      }
    } catch (error) {
      // Payment failed - call unsubscribe API
      await callUnsubscribeAPI(ccId, false);
      await showError('Error!', 'Payment failed. Please try again.', SWAL_CONFIG);
    }
  }, [subscriptionPrice, creatorInfo, callUnsubscribeAPI, promotionData, purchasePayload, handleSubscriptionSuccess]);

  // Show payment confirmation dialog
  const showPaymentConfirmation = useCallback(async (paymentTokenId, subscriptionData, ccId, purchasePayload) => {
    const confirmed = await confirmDialog(
      'Confirm Payment',
      `You have a saved payment method. Would you like to subscribe for $${formatAmountWithTwoDecimalPlaces(purchasePayload?.amount) ?? subscriptionPrice}?`,
      {
        confirmButtonText: 'Yes, Subscribe',
        cancelButtonText: 'Cancel',
        cancelButtonColor: '#d33',
        ...SWAL_CONFIG
      }
    );
    if (confirmed) {
      chargeWithExistingToken(paymentTokenId, subscriptionData, ccId);
    } else {
      closeAlert();
    }
  }, [subscriptionPrice, chargeWithExistingToken]);

  // No saved card: open the CCBill card form WITHOUT calling
  // POST /content-creator/:id/subscriptions first — whether this is a free
  // trial (amount 0) or a paid subscription. Only the bundles lookup (GET)
  // runs here; the subscription itself is created on card-form submit in
  // prepareDeferredPaymentForm, so opening/closing the form creates nothing.
  const openDeferredCCBillModal = useCallback(async (ccId) => {
    const bundlesResult = await getBundlesInfo(promotionData, purchasePayload);
    if (!bundlesResult.success || !bundlesResult.data) {
      closeAlert();
      await showError('Error!', bundlesResult.error || 'No monthly subscription plan available.', SWAL_CONFIG);
      return;
    }

    pendingSubscriptionDataRef.current = bundlesResult.data;
    deferredPaymentFormRef.current = null;
    subscriptionCreatedRef.current = false;
    setPaymentForm(null);
    setIsDeferredCCBill(true);
    closeAlert();
    setCCBillModalOpen(true);
  }, [promotionData, purchasePayload]);

  // Called by CCBillModal only after the user has filled and submitted the
  // card form. This is the first point at which POST subscriptions may run
  // for a user with no saved card. The result is cached, so retrying after a
  // declined charge reuses the same subscription instead of creating another.
  const prepareDeferredPaymentForm = useCallback(async () => {
    if (deferredPaymentFormRef.current) {
      return { success: true, paymentForm: deferredPaymentFormRef.current };
    }

    const ccId = creatorInfo?._id;
    const subscriptionData = pendingSubscriptionDataRef.current;
    if (!ccId || !subscriptionData) {
      return { success: false, error: 'Subscription details are missing. Please reopen and try again.' };
    }

    try {
      const subscriptionResult = await createSubscription(ccId, subscriptionData);
      if (!subscriptionResult.success || !subscriptionResult.data) {
        return { success: false, error: subscriptionResult.error || 'Failed to create subscription. Please try again.' };
      }

      // One-click payment needs no card form — hand it to the existing handler.
      if (subscriptionResult.data?.paymentOneClick === '1') {
        subscriptionCreatedRef.current = true;
        deferredPaymentFormRef.current = subscriptionResult.data.paymentForm || null;
        await handleCCBillPayment(subscriptionResult, deferredPaymentFormRef.current);
        return { success: false, silent: true };
      }

      const paymentForm = subscriptionResult.data.paymentForm;
      if (!paymentForm || subscriptionResult.data?.paymentGateway !== 'CCBILL') {
        // Roll back the just-created subscription — no charge will follow.
        await callUnsubscribeAPI(ccId, true);
        return { success: false, error: 'Unsupported payment gateway. Please try again.' };
      }

      subscriptionCreatedRef.current = true;
      deferredPaymentFormRef.current = paymentForm;
      setPaymentForm(paymentForm);
      return { success: true, paymentForm };
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.response?.data?.exceptionName || 'Something went wrong. Please try again.';
      if (status === 422 || status === 401) {
        setCCBillModalOpen(false);
        await showInfo('Sorry!', message, SWAL_CONFIG).then(() => {
          window.location.href = `/${creatorInfo?.username}`;
        });
        return { success: false, silent: true };
      }
      return { success: false, error: message };
    }
  }, [creatorInfo, handleCCBillPayment, callUnsubscribeAPI]);

  // Check existing payment token
  const checkExistingPaymentToken = useCallback(async (ccId) => {
    try {
      // Check account-information API for paymentTokenId
      const accountResponse = await userService.fetchUser();

      let existingPaymentTokenId = null;

      // Check if account-information has paymentTokenId
      if (accountResponse?.success && accountResponse?.data?.paymentTokenId) {
        existingPaymentTokenId = accountResponse.data.paymentTokenId;
      }

      // No saved card: show the card form only. Do NOT create a subscription
      // yet (free trial or paid) — that happens on form submit.
      if (!existingPaymentTokenId || existingPaymentTokenId === 'null') {
        await openDeferredCCBillModal(ccId);
        return;
      }

      // If paymentTokenId exists, get subscription data and show confirmation.
      // The subscription is only created after the user confirms payment.
      const bundlesResult = await getBundlesInfo(promotionData, purchasePayload);
      if (bundlesResult.success && bundlesResult.data) {
        const subscriptionData = bundlesResult.data;

        // Show payment confirmation with existing token
        showPaymentConfirmation(existingPaymentTokenId, subscriptionData, ccId, purchasePayload);
      } else {
        closeAlert();
        await showError('Error!', bundlesResult.error || 'No monthly subscription plan available.', SWAL_CONFIG);
      }
    } catch (error) {
      // If verification fails, fall back to the card form (still deferred)
      await openDeferredCCBillModal(ccId);
    }
  }, [promotionData, showPaymentConfirmation, purchasePayload, openDeferredCCBillModal]);

  // Send callbox (initiate subscription process)
  const sendCallbox = useCallback(async () => {
    // Close the current modal first
    toggleModal();

    // Add a small delay to allow modal cleanup before showing loading alert
    await new Promise(resolve => setTimeout(resolve, 100));
    const ccId = creatorInfo?._id;

    // Free trial also requires a card, so it goes through the same payment flow
    if (isFreeTrialSubscription) {
      showFreeTrialLoading();
    } else {
      showPaymentLoading();
    }

    // First check if already subscribed
    const checkResult = await checkSubscription(ccId);

    if (checkResult.subscribed) {
      // Already subscribed
      await showInfo(
        'Already Subscribed!',
        'You are already subscribed to this content creator.',
        SWAL_CONFIG
      ).then(() => {
        window.location.href = `/${creatorInfo?.username}`;
      });
      return;
    }
    // Not subscribed, continue with subscription flow

    // Check for existing payment token
    checkExistingPaymentToken(ccId);
  }, [creatorInfo, toggleModal, checkExistingPaymentToken, isFreeTrialSubscription]);

  // Close CCBill modal
  useEffect(() => {
    if (!ccBillModalOpen && paymentForm) {
      const timeoutId = setTimeout(() => {
        setPaymentForm(null);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [ccBillModalOpen, paymentForm]);

  // Only roll back when a subscription was actually created (i.e. the card
  // form was submitted). Closing the form before submitting must not call
  // any subscription API at all.
  const cleanupDeferredSubscription = useCallback(async (isManualRemove) => {
    const ccId = creatorInfo?._id;
    if (subscriptionCreatedRef.current && ccId) {
      await callUnsubscribeAPI(ccId, isManualRemove);
    }
    subscriptionCreatedRef.current = false;
    deferredPaymentFormRef.current = null;
    pendingSubscriptionDataRef.current = null;
    setIsDeferredCCBill(false);
  }, [creatorInfo, callUnsubscribeAPI]);

  const closeCCBillModal = useCallback(async () => {
    await cleanupDeferredSubscription(false);
    setCCBillModalOpen(false);
  }, [cleanupDeferredSubscription]);

  const cancelCCBillModal = useCallback(async () => {
    await cleanupDeferredSubscription(true);
    setCCBillModalOpen(false);
  }, [cleanupDeferredSubscription]);

  // Handle payment success
  const onPaymentSuccess = useCallback(async () => {
    await handleSubscriptionSuccess();
  }, [handleSubscriptionSuccess]);

  // Render button based on user role
  const renderButton = () => {
    const role = creatorInfo?.rol;
    if (role === ROLE.CREATOR_SUB) {
      return (
        <Button
          className={`btn-round ${isSubscribed && !isOfferActiveOfUser ? 'cm_subscribed_btn' : 'subscribe-btn subscribe-btn-fullwidth'} `}
          color="danger"
          outline
          type="button"
          onClick={isButtonClickable ? loggedIn ? toggleModal : subscribeBtn : null}
        >
          {btnText || ''}
        </Button>
      );
    }

    return null;
  };

  // Render loading button helper
  const renderLoadingButton = (onClick, buttonText, disabled) => (
    <button
      onClick={onClick}
      className="subscribe-modal-btn"
      disabled={disabled}
    >
      {disabled ? (
        <span>
          <i className="fa-solid fa-spinner fa-spin subscribe-modal-spinner"></i>
          Loading...
        </span>
      ) : (
        buttonText
      )}
    </button>
  );

  const renderTriggerContent = () => {
    if (typeof renderTrigger === 'function') {
      return renderTrigger({
        onClick: toggleModal,
        isDisabled: !isButtonClickable,
        isButtonClickable,
        loggedIn,
        subscribeBtn,
        btnText: btnText || 'Subscribe',
      });
    }

    return renderButton();
  };

  const isShowPinnedMessage = !isRenew && !isFreeTrialSubscription && !getPrice.isFreeTrial && !isOfferActiveOfUser && !promotionData?.offer_type;

  const modalMarkup = creatorInfo?.old_account !== true ? (
    <div
      id="subscribe-modal"
      className={`subscribe-modal-overlay cm_bg_blur ${modal ? 'subscribe-modal-show' : 'subscribe-modal-hide'}`}
    >
      <div className="subscribe-modal-container">
        {/* Header Image */}
        <div className="subscribe-modal-header">
          <img
            className="subscribe-modal-header-img"
            src={creatorInfo?.profile_banner || defaultProfileBanner}
            alt="Creator header"
          />
          <div className="subscribe-modal-header-overlay"></div>

          {/* Close Button */}
          <button
            onClick={toggleModal}
            className="subscribe-modal-close-btn close-bright"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Profile Section */}
        <div className="subscribe-modal-content">
          <div className="subscribe-modal-profile">
            <img
              src={creatorInfo?.profile_picture || DEFAULT_USER_AVATAR}
              alt="Creator Avatar"
              className="subscribe-modal-avatar"
            />
            <div>
              <h3 className="subscribe-modal-username">@{creatorInfo?.username}</h3>
            </div>
          </div>

          {/* Pinned Message */}
          {isShowPinnedMessage && creatorInfo?.pinMessage && (() => {
            const PINNED_TRUNCATE_THRESHOLD = 50;
            const PINNED_VISIBLE_LENGTH = 55; // including "…"
            const shouldTruncate = creatorInfo.pinMessage.length > PINNED_TRUNCATE_THRESHOLD;
            const displayText = shouldTruncate && !pinnedMessageExpanded
              ? `${creatorInfo.pinMessage.slice(0, PINNED_VISIBLE_LENGTH - 1)}…`
              : creatorInfo.pinMessage;
            return (
              <div className="subscribe-modal-pinned-message">
                <img
                  src={creatorInfo?.profile_picture || DEFAULT_USER_AVATAR}
                  alt="Creator Avatar"
                  className="subscribe-modal-pinned-avatar"
                />
                <div className="subscribe-modal-pinned-content">
                  <p className="subscribe-modal-pinned-text">{displayText}</p>
                  {shouldTruncate && (
                    <button
                      type="button"
                      className="subscribe-modal-pinned-show-more"
                      onClick={() => setPinnedMessageExpanded((e) => !e)}
                    >
                      {pinnedMessageExpanded ? 'show less' : 'show more'}
                    </button>
                  )}
                  <p className="subscribe-modal-pinned-label">Pinned Message</p>
                </div>
              </div>
            );
          })()}

          {/* Benefits List */}
          <div className="subscribe-modal-benefits">
            <div className="subscribe-modal-benefit-item">
              <div className="subscribe-modal-benefit-icon">
                <i className="fa-solid fa-check"></i>
              </div>
              <div>
                <p className="subscribe-modal-benefit-title">All Access Pass</p>
                <p className="subscribe-modal-benefit-desc">Unlock all premium content</p>
              </div>
            </div>

            <div className="subscribe-modal-benefit-item">
              <div className="subscribe-modal-benefit-icon">
                <i className="fa-solid fa-check"></i>
              </div>
              <div>
                <p className="subscribe-modal-benefit-title">Let&apos;s Talk</p>
                <p className="subscribe-modal-benefit-desc">Start an exclusive 1-on-1 chat with this creator</p>
              </div>
            </div>

            <div className="subscribe-modal-benefit-item">
              <div className="subscribe-modal-benefit-icon">
                <i className="fa-solid fa-check"></i>
              </div>
              <div>
                <p className="subscribe-modal-benefit-title">Cancel Anytime</p>
                <p className="subscribe-modal-benefit-desc">No long-term commitments required</p>
              </div>
            </div>
          </div>

          {/* Free Trial Subscription */}
          {isFreeTrialSubscription && getPrice.isFreeTrial === true && (
            <div className="subscribe-modal-renewal-info">
              <h4 className="subscribe-modal-renewal-title">Subscription Renewal</h4>
              <p className="subscribe-modal-renewal-text">
                <strong>@{creatorInfo?.username}</strong>{' '}
                is offering a <strong>{getPrice.displayTextSingular || getPrice.displayText}</strong> trial.
                After the trial expires, your subscription will automatically renew at{' '}
                <strong>${subscriptionPriceWithExtraCharge.toFixed(2)}</strong> per month unless you cancel.
              </p>
            </div>
          )}

          {/* Discount Subscription */}
          {!isFreeTrialSubscription && !isOfferActiveOfUser &&
            promotionData && promotionData.offer_type === OFFER_TYPE.FIRST_MONTH && (
              <div className="subscribe-modal-renewal-info">
                <h4 className="subscribe-modal-renewal-title">Subscription Renewal</h4>
                <p className="subscribe-modal-renewal-text">
                  <strong>@{creatorInfo?.username}</strong>{' '}
                  is offering a 30 day discount. After it expires,
                  your subscription will automatically renew at{' '}
                  <strong>${subscriptionPriceWithExtraCharge.toFixed(2)}</strong> per month unless you cancel.
                </p>
              </div>
            )}

          {/* Renew Subscription */}
          {/* {(isRenew || btnText === 'Renew') && (
            <div className="subscribe-modal-renewal-info">
              <h4 className="subscribe-modal-renewal-title">Subscription Renewal</h4>
              <p className="subscribe-modal-renewal-text">
                Please confirm you want to renew the subscription to{' '}
                <strong>@{creatorInfo?.username}</strong> for{' '}
                <strong>${formatAmountWithTwoDecimalPlaces(purchasePayload?.amount) ?? subscriptionPriceWithExtraCharge.toFixed(2)}</strong>{' '}
                per month.
              </p>
            </div>
          )} */}

          {/* Subscribe Button */}
          {!isRenew ? (
            <>
              {loggedIn ? (
                renderLoadingButton(
                  sendCallbox,
                  getPrice.isFreeTrial === true
                    ? getPrice.displayText
                    : (promotionData && promotionData.offer_type === OFFER_TYPE.FIRST_MONTH && !isOfferActiveOfUser)
                      ? `Redeem for $${formatAmountWithTwoDecimalPlaces(purchasePayload?.amount) ?? getPrice.currentPrice ?? subscriptionPrice}`
                      : `Subscribe for $${formatAmountWithTwoDecimalPlaces(purchasePayload?.amount) ?? subscriptionPrice}/mo`,
                  isPromotionLoading
                )
              ) : (
                renderLoadingButton(
                  subscribeBtn,
                  getPrice.isFreeTrial === true
                    ? getPrice.displayText
                    : (promotionData && promotionData.offer_type === OFFER_TYPE.FIRST_MONTH && !isOfferActiveOfUser)
                      ? `Redeem for $${formatAmountWithTwoDecimalPlaces(purchasePayload?.amount) ?? getPrice.currentPrice ?? subscriptionPrice}`
                      : `Subscribe for $${formatAmountWithTwoDecimalPlaces(purchasePayload?.amount) ?? subscriptionPrice}/mo`,
                  isPromotionLoading
                )
              )}
            </>
          ) : (
            <>
              {loggedIn ? (
                renderLoadingButton(
                  sendCallbox,
                  'Confirm Renewal',
                  isPromotionLoading
                )
              ) : (
                renderLoadingButton(
                  subscribeBtn,
                  'Subscribe',
                  isPromotionLoading
                )
              )}
            </>
          )}

          {getPrice.isFreeTrial === false && (
            <p className="subscribe-modal-vat-text">
              {/* ? `VAT fee of $${purchasePayload.vatAmount.toFixed(2)} has been applied.` */}
              {getVatAndServiceFeeAppliedText(purchasePayload?.vatAmount ?? 0)}
            </p>
          )}

          <div className="modern-login-divider"></div>
          <p className="subscribe-modal-security-text">
            <i className="fa-solid fa-shield-halved"></i>
            Secure payment powered by CCBill
          </p>
        </div>
      </div>
    </div>
  ) : null;


  return (
    <>
      {renderTriggerContent()}  {/* show Subscribe button here */}

      {/* Subscribe Modal */}
      {isBrowser && modal && modalMarkup && createPortal(modalMarkup, document.body)}

      {/* CCBill Modal — in deferred mode paymentForm is still null when the
          card form opens; it is created on submit via onBeforeCharge */}
      {ccBillModalOpen && (paymentForm || isDeferredCCBill) && (
        <CCBillModal
          paymentForm={paymentForm}
          openModal={ccBillModalOpen}
          toggleModal={closeCCBillModal}
          onCancel={cancelCCBillModal}
          onSuccess={onPaymentSuccess}
          onBeforeCharge={isDeferredCCBill ? prepareDeferredPaymentForm : null}
          creatorInfo={creatorInfo}
          purchaseAmount={purchasePayload?.amount ?? subscriptionPrice}
          vatAmount={purchasePayload?.vatAmount ?? 0}
          vatPercentage={purchasePayload?.vatPercentage ?? 0}
          purchasePayload={purchasePayload}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
  };
};
export default connect(mapStateToProps)(SubscribeModalLoggedIn);